import { FC } from 'react';
import { getBezierPath, EdgeLabelRenderer, BaseEdge } from 'reactflow';
import type { EdgeProps } from 'reactflow';
import { ArrowRight, Database } from 'lucide-react';

export interface DataFlowEdgeData {
  data_type?: string;
  data_mapping?: string;
  transformation?: string;
  description?: string;
}

const DataFlowEdge: FC<EdgeProps<DataFlowEdgeData>> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  data = {},
  selected,
  markerEnd,
}) => {
  const [edgePath, labelX, labelY] = getBezierPath({
    sourceX,
    sourceY,
    sourcePosition,
    targetX,
    targetY,
    targetPosition,
  });

  const hasDataTransformation = data.transformation || data.data_mapping;

  // Purple for transformed data, gray for plain data
  const strokeColor = hasDataTransformation ? '#8B5CF6' : '#6B7280';

  return (
    <>
      <BaseEdge
        id={id}
        path={edgePath}
        markerEnd={markerEnd}
        style={{
          stroke: strokeColor,
          strokeWidth: selected ? 4 : 3,
          strokeDasharray: hasDataTransformation ? '0' : '4,4',
          opacity: selected ? 1 : 1,
        }}
      />

      {(data.data_type || data.data_mapping || data.transformation) && (
        <EdgeLabelRenderer>
          <div
            style={{
              position: 'absolute',
              transform: `translate(-50%, -50%) translate(${labelX}px,${labelY}px)`,
              pointerEvents: 'all',
            }}
            className="nodrag nopan group"
          >
            {/* Label Badge */}
            <div
              className="
                flex items-center gap-1 
                px-2.5 py-1.5 
                bg-card
                border border-border dark:border-border-subtle
                text-xs font-semibold 
                rounded-lg shadow-sm
                transition-all duration-fast
                hover:scale-105 hover:shadow-md
              "
              style={{
                color: hasDataTransformation ? '#8B5CF6' : '#6B7280',
                borderColor: hasDataTransformation ? '#8B5CF6' : undefined,
              }}
            >
              {hasDataTransformation ? (
                <ArrowRight className="w-3 h-3" />
              ) : (
                <Database className="w-3 h-3" />
              )}
              <span className="text-foreground">
                {data.data_type || 'Data'}
              </span>
            </div>

            {/* Hover Tooltip */}
            {(data.data_mapping || data.transformation || data.description) && (
              <div
                className="
                  absolute top-10 left-1/2 -translate-x-1/2
                  bg-popover
                  text-xs px-3 py-2.5 
                  rounded-lg shadow-lg
                  border border-border dark:border-border-subtle
                  opacity-0 group-hover:opacity-100
                  transition-opacity duration-fast
                  pointer-events-none
                  z-50
                  min-w-max max-w-xs
                  space-y-1.5
                "
              >
                {data.data_mapping && (
                  <div className="flex items-start gap-2">
                    <span className="text-category-diagnostics font-medium">Mapping:</span>
                    <span className="text-foreground">
                      {data.data_mapping}
                    </span>
                  </div>
                )}
                {data.transformation && (
                  <div className="flex items-start gap-2">
                    <span className="text-accent-blue font-medium">Transform:</span>
                    <span className="text-foreground">
                      {data.transformation}
                    </span>
                  </div>
                )}
                {data.description && (
                  <div className="text-muted-foreground pt-1 border-t border-border-subtle">
                    {data.description}
                  </div>
                )}
              </div>
            )}
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
};

export default DataFlowEdge;

